import { isNil, max, min, range } from 'lodash';
import { RenderElementProps } from 'slate-react';

export interface TableOptionsProps {
  isReadOnly?: boolean;
  isTableCompletion?: boolean;
  isTableCompletionV2?: boolean;
  hasCaption?: boolean;
}

export interface TableBaseProps extends RenderElementProps, TableOptionsProps {}

export const isTouchEvent = (event: MouseEvent | TouchEvent): event is TouchEvent => {
  return Boolean((event as TouchEvent).touches && (event as TouchEvent).touches.length);
};

export const isMouseEvent = (event: MouseEvent | TouchEvent): event is MouseEvent => {
  return Boolean(
    ((event as MouseEvent).clientX || (event as MouseEvent).clientX === 0) &&
      ((event as MouseEvent).clientY || (event as MouseEvent).clientY === 0)
  );
};

export const getCellFromTableByIndex = ({ tableNode, rowIndex, colIndex }) => {
  const tableChildren = tableNode?.children || [];

  if (tableChildren.length === 1)
    return {
      rowSpan: 1,
      colSpan: 1,
    };

  const rowNode = tableChildren[rowIndex] || tableChildren[tableChildren.length - 1];
  const rowChildren = rowNode?.children || [];

  return rowChildren[colIndex] || rowChildren[rowChildren.length - 1];
};

export const getSelectionRangeBound = ({ startPosition, endPosition, tableNode }) => {
  let topLeft = null;
  let bottomRight = null;
  if (startPosition && endPosition && tableNode) {
    const minRow = min([startPosition.rowIndex, endPosition.rowIndex]);
    const maxRow = max([startPosition.rowIndex, endPosition.rowIndex]);
    const minCol = min([startPosition.columnIndex, endPosition.columnIndex]);
    const maxCol = max([startPosition.columnIndex, endPosition.columnIndex]);

    topLeft = {
      rowIndex: minRow,
      columnIndex: minCol,
    };

    bottomRight = {
      rowIndex: maxRow,
      columnIndex: maxCol,
    };

    range(minRow, maxRow + 1).forEach((rowIndex) => {
      range(minCol, maxCol + 1).forEach((colIndex) => {
        const cell = getCellFromTableByIndex({
          tableNode,
          rowIndex,
          colIndex,
        });
        const colSpan = cell?.colSpan ? cell.colSpan - 1 : 0;
        const rowSpan = cell?.rowSpan ? cell.rowSpan - 1 : 0;
        // Flow: without ref
        // update bottom right bound
        if (colIndex + colSpan > bottomRight.columnIndex) {
          bottomRight.columnIndex = colIndex + colSpan;
        }
        if (rowIndex + rowSpan > bottomRight.rowIndex) {
          bottomRight.rowIndex = rowIndex + rowSpan;
        }

        if (colIndex < topLeft.columnIndex) {
          topLeft.columnIndex = Math.max(colIndex, 0);
        }
        if (rowIndex < topLeft.rowIndex) {
          topLeft.rowIndex = Math.max(rowIndex, 0);
        }

        // Flow: with row, col ref
        // Check ref
        if (!isNil(cell?.rowRef) || !isNil(cell?.colRef)) {
          const cellRef = getCellFromTableByIndex({
            tableNode,
            rowIndex: cell.rowRef,
            colIndex: cell.colRef,
          });
          const refRowSpan = cellRef?.rowSpan ? cellRef.rowSpan - 1 : 0;
          const refColSpan = cellRef?.colSpan ? cellRef.colSpan - 1 : 0;
          // update bottom right bound
          if (cell.colRef + refColSpan > bottomRight.columnIndex) {
            bottomRight.columnIndex = cell.colRef + refColSpan;
          }
          if (cell.rowRef + refRowSpan > bottomRight.rowIndex) {
            bottomRight.rowIndex = cell.rowRef + refRowSpan;
          }

          // update top left bound
          if (cell.rowRef < topLeft.rowIndex) {
            topLeft.rowIndex = Math.max(cell.rowRef, 0);
          }
          if (cell.colRef < topLeft.columnIndex) {
            topLeft.columnIndex = Math.max(cell.colRef, 0);
          }
        }
      });
    });
  }

  return {
    topLeft,
    bottomRight,
  };
};

export const getStartEndCellPositionFromRowIndex = ({ tableNode, rowIndex, columnCount }) => {
  const startCell = getCellFromTableByIndex({
    tableNode,
    rowIndex,
    colIndex: 0,
  });
  const endCell = getCellFromTableByIndex({
    tableNode,
    rowIndex,
    colIndex: columnCount - 1,
  });

  const startPosition = {
    rowIndex,
    columnIndex: 0,
    rowSpan: startCell.rowSpan,
    colSpan: startCell.colSspan,
  };
  const endPosition = {
    rowIndex,
    columnIndex: columnCount - 1,
    rowSpan: endCell.rowSpan,
    colSpan: endCell.colSspan,
  };

  return { startPosition, endPosition };
};

export const getStartEndCellPositionFromColumnIndex = ({ tableNode, columnIndex, rowCount }) => {
  const startCell = getCellFromTableByIndex({
    tableNode,
    rowIndex: 0,
    colIndex: columnIndex,
  });
  const endCell = getCellFromTableByIndex({
    tableNode,
    rowIndex: rowCount - 1,
    colIndex: columnIndex,
  });
  const startPosition = {
    rowIndex: 0,
    columnIndex,
    rowSpan: startCell.rowSpan,
    colSpan: startCell.colSspan,
  };
  const endPosition = {
    rowIndex: rowCount - 1,
    columnIndex,
    rowSpan: endCell.rowSpan,
    colSpan: endCell.colSspan,
  };

  return { startPosition, endPosition };
};
